import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { AppText, AppIcon, Tooltip, Flex, IconButton, styled, useTheme, useSnackbar, Snackbar, } from '@streem/ui-react';
import { useRoomSession } from '../../../../context/room_session';
import { logger } from '../../../../util/logger';
import { useObservable } from '../../../../hooks/rxjs';
import { useCompanySettings } from '../../../../context/company_settings';
import { streem } from 'streem-sdk-protobuf';
export const RecordingIndicator = ({ trayOpen }) => {
    const roomSession = useRoomSession();
    const companySettings = useCompanySettings();
    const theme = useTheme();
    const [recordingStatus] = useObservable(roomSession.recording.recordingStatus);
    const { addNotification, snackbarNotifications, removeNotification } = useSnackbar();
    const [recordingIndicatorMessage, setRecordingIndicatorMessage] = useState('');
    const [hover, setHover] = useState(false);
    const pausedRecordingId = 'pausedRecording';
    const recordingUnavailableErrorId = 'recordingUnavailableError';
    const recordingIndicatorWidth = companySettings?.allowPauseResumeRecording ? '145px' : '113px';
    const createErrorRecordingSnackItem = useCallback((id, message) => {
        removeNotification(id);
        addNotification({
            id: id,
            message: message,
            icon: _jsx(AppIcon, { name: "AlertIcon", color: theme.colors.red50, size: "small" }),
            useCloseButton: true,
            onActionClick: () => removeNotification(id),
        });
    }, [addNotification, removeNotification, theme.colors.red50]);
    const handlePauseRecording = useCallback(async () => {
        const pauseRecordingApiErrorId = 'pauseRecordingApiError';
        try {
            await roomSession.recording.pauseRecording();
            removeNotification(pauseRecordingApiErrorId);
        }
        catch (error) {
            createErrorRecordingSnackItem(pauseRecordingApiErrorId, 'Pause Recording Error. Please try again.');
            logger.error('Error making pause recording request', error);
        }
    }, [roomSession, removeNotification, createErrorRecordingSnackItem]);
    const handleResumeRecording = useCallback(async () => {
        const resumeRecordingApiErrorId = 'resumeRecordingApiError';
        try {
            await roomSession.recording.resumeRecording();
            removeNotification(resumeRecordingApiErrorId);
        }
        catch (error) {
            createErrorRecordingSnackItem(resumeRecordingApiErrorId, 'Resume Recording Error. Please try again.');
            logger.error('Error making resume recording request', error);
        }
    }, [roomSession, removeNotification, createErrorRecordingSnackItem]);
    const createResumeRecordingSnackItem = useCallback(() => {
        removeNotification(recordingUnavailableErrorId);
        addNotification({
            id: pausedRecordingId,
            message: 'The recording has been paused.',
            icon: _jsx(AppIcon, { name: "PauseRecordingIcon", color: theme.colors.grey30, iconSize: "28px" }),
            actionMessage: 'Resume',
            onActionClick: () => handleResumeRecording(),
        });
    }, [addNotification, removeNotification, handleResumeRecording, theme.colors.grey30]);
    useEffect(() => {
        if (!recordingStatus) {
            return;
        }
        if (recordingStatus === streem.api.Artifact.RecordingStatus.RECORDING_STATUS_NONE ||
            recordingStatus === streem.api.Artifact.RecordingStatus.RECORDING_STATUS_RESUMED) {
            setRecordingIndicatorMessage('Recording');
            removeNotification(pausedRecordingId);
            removeNotification(recordingUnavailableErrorId);
        }
        if (recordingStatus === streem.api.Artifact.RecordingStatus.RECORDING_STATUS_PAUSED) {
            setRecordingIndicatorMessage('Paused');
            createResumeRecordingSnackItem();
        }
        if (recordingStatus ===
            streem.api.Artifact.RecordingStatus.RECORDING_STATUS_TEMPORARY_FAILURE) {
            setRecordingIndicatorMessage('Recording Unavailable');
            createErrorRecordingSnackItem(pausedRecordingId, 'Recording error. Call still connected.');
            logger.error('Temporary failure reported for recording status');
        }
    }, [
        recordingStatus,
        removeNotification,
        createResumeRecordingSnackItem,
        createErrorRecordingSnackItem,
    ]);
    if (!recordingStatus) {
        return null;
    }
    const isRecording = [
        streem.api.Artifact.RecordingStatus.RECORDING_STATUS_NONE,
        streem.api.Artifact.RecordingStatus.RECORDING_STATUS_RESUMED,
    ].includes(recordingStatus);
    const isTemporaryFailure = recordingStatus === streem.api.Artifact.RecordingStatus.RECORDING_STATUS_TEMPORARY_FAILURE;
    return (_jsxs(RecordingIndicatorWrapper, { "data-testid": "recording-indicator", isTemporaryFailure: isTemporaryFailure, recordingIndicatorWidth: recordingIndicatorWidth, children: [!isTemporaryFailure && (_jsx(ItemContainer, { marginLeft: "12px", children: _jsx(AppIcon, { name: "RecordingIcon", iconSize: "18px", color: isRecording ? theme.colors.red50 : theme.colors.grey40 }) })), _jsx(ItemContainer, { margin: "0 0 0 9px", children: _jsx(AppText, { color: "white", style: { fontSize: '14px' }, children: recordingIndicatorMessage }) }), companySettings?.allowPauseResumeRecording && isRecording && (_jsx(ItemContainer, { children: _jsx(Tooltip, { showTooltip: hover, placement: "bottom-end", fallbackPlacements: ['left', 'top'], message: _jsx(AppText, { size: "small", children: "Pause Recording" }), children: _jsx(IconButton, { iconName: "PauseRecordingIcon", label: "Pause Recording", background: 'grey80', fill: "white", onMouseEnter: () => setHover(true), onMouseOver: () => setHover(true), onMouseOut: () => setHover(false), onClick: handlePauseRecording }) }) })), _jsx(Snackbar, { content: snackbarNotifications, position: { vertical: 'top', horizontal: 'center' }, isTrayOpen: trayOpen })] }));
};
const RecordingIndicatorWrapper = styled(Flex)(({ isTemporaryFailure, recordingIndicatorWidth, theme }) => ({
    display: 'flex',
    width: isTemporaryFailure ? '157px' : recordingIndicatorWidth,
    height: '42px',
    borderRadius: '4px',
    alignItems: 'center',
    position: 'absolute',
    zIndex: 9999,
    background: theme.colors.grey80,
    top: 15,
    left: 12,
}));
const ItemContainer = styled(Flex)(() => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));
//# sourceMappingURL=recording_indicator.js.map